import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { fadeInUp } from '@/lib/motion';
import { BlueprintSidebar } from '@/components/blueprint/BlueprintSidebar';
import {
  BlueprintDetailPanel,
  BlueprintEmptyState,
  BlueprintLoadErrorState,
} from '@/components/blueprint/BlueprintDetailPanel';
import { BlueprintCreatePanel } from '@/pages/blueprints/BlueprintCreatePage';
import { SnapshotDetailPanel } from '@/components/blueprint/SnapshotDetailPanel';
import { TwoPanelLayout } from '@reflex/ui/components/layout/TwoPanelLayout';
import { useBlueprint, useBlueprints } from '@/hooks/blueprints/useBlueprints';
import { useSnapshots } from '@/hooks/snapshots/useSnapshots';
import { useTwoPanelState } from '@/hooks/ui/useTwoPanelState';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import type { Blueprint, Snapshot } from '@/lib/types';

/** Runloop disk-snapshot ids are `snp_`-prefixed; blueprints are `bpt_`. */
function isSnapshotId(id: string | undefined): boolean {
  return Boolean(id?.startsWith('snp_'));
}

/**
 * @param create When true (route `blueprints/new`), the detail area renders the
 *   blueprint create form instead of a selected blueprint, keeping the sidebar
 *   list on the left so create reads as just another detail view.
 */
export function BlueprintsPage({ create = false }: { create?: boolean } = {}) {
  const { blueprintId } = useParams<{ blueprintId: string }>();
  const navigate = useNavigate();
  const panel = useTwoPanelState();
  const blueprintsQuery = useBlueprints();
  const liveLoadFailed = blueprintsQuery.isFallback && blueprintsQuery.source === 'live-fallback';
  const blueprints = liveLoadFailed ? [] : (blueprintsQuery.data ?? []);
  const routes = useOrgRoutes();

  // Snapshots ride the same `/blueprints/:blueprintId` route — the id prefix
  // disambiguates them from blueprints, so no separate route is needed.
  const snapshotSelected = isSnapshotId(blueprintId);
  const { data: snapshots = [] } = useSnapshots();
  const freshSnapshot = snapshotSelected
    ? (snapshots.find((s) => s.id === blueprintId) ?? null)
    : null;

  const listMatch =
    blueprintId && !snapshotSelected
      ? (blueprints.find((bp) => bp.id === blueprintId) ?? null)
      : null;
  const { data: directBlueprint } = useBlueprint(
    liveLoadFailed || listMatch || snapshotSelected ? undefined : blueprintId,
  );
  const freshBlueprint = listMatch ?? directBlueprint ?? null;
  const selectedId = freshSnapshot?.id ?? freshBlueprint?.id ?? null;

  const handleDeselect = useCallback(() => navigate(routes.blueprints), [navigate, routes]);

  const handleSelect = useCallback(
    (blueprint: Blueprint) => {
      navigate(routes.blueprint(blueprint.id));
      panel.closeSidebar();
    },
    [navigate, panel, routes],
  );

  const handleSelectSnapshot = useCallback(
    (snapshot: Snapshot) => {
      navigate(routes.blueprint(snapshot.id));
      panel.closeSidebar();
    },
    [navigate, panel, routes],
  );

  return (
    <TwoPanelLayout
      autoSaveId="blueprints-sidebar"
      sidebarCollapsed={panel.collapsed}
      onSidebarCollapsedChange={panel.setCollapsed}
      sidebar={
        <BlueprintSidebar
          selectedId={selectedId}
          onSelect={handleSelect}
          onSelectSnapshot={handleSelectSnapshot}
          onDeselect={handleDeselect}
          collapsed={panel.collapsed}
          onToggle={panel.toggleCollapsed}
        />
      }
      mobileSidebar={
        <BlueprintSidebar
          selectedId={selectedId}
          onSelect={handleSelect}
          onSelectSnapshot={handleSelectSnapshot}
          onDeselect={handleDeselect}
          collapsed={false}
          onToggle={panel.closeSidebar}
          mobile
        />
      }
      sidebarOpen={panel.sidebarOpen}
      onSidebarOpenChange={panel.setSidebarOpen}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={create ? 'create' : (selectedId ?? 'empty')}
          className="flex h-full min-h-0 flex-col"
          {...fadeInUp}
        >
          {liveLoadFailed ? (
            <BlueprintLoadErrorState message={blueprintsQuery.error?.message} />
          ) : create ? (
            <BlueprintCreatePanel />
          ) : freshSnapshot ? (
            <SnapshotDetailPanel snapshot={freshSnapshot} onDeleted={handleDeselect} />
          ) : freshBlueprint ? (
            <BlueprintDetailPanel
              blueprint={freshBlueprint}
              onDeleted={handleDeselect}
              onSelectBlueprint={handleSelect}
            />
          ) : (
            <BlueprintEmptyState onCreate={() => navigate(routes.blueprintNew)} />
          )}
        </motion.div>
      </AnimatePresence>
    </TwoPanelLayout>
  );
}
